// Rôles et droits (maquette Console « Rôles et droits ») — le MODÈLE de droits de l'éditeur.
//
// CE QUE L'ÉCRAN DOIT FAIRE COMPRENDRE, et qui n'est pas évident : ces rôles sont des DÉFAUTS partagés.
// Un client qui les ajuste chez lui s'en DÉTACHE — il ne reçoit plus les modifications faites ici. D'où
// deux chiffres affichés partout (« suivent » / « personnalisé ») et un bloc de divergence : sans eux,
// on modifie un rôle en croyant toucher tout le monde alors qu'on n'atteint qu'une partie du parc.
//
// ⚠️ LE CATALOGUE DES MODULES VIENT DU SERVEUR. Le recopier ici ferait diverger l'écran et la
// validation : on proposerait une case que le serveur refuse, et l'utilisateur ne comprendrait pas
// pourquoi son enregistrement échoue.
//
// ⚠️ « ÉCRITURE » NE SE DIT PAS SUR UN MODULE NON GRADUÉ. Il s'ouvre ou il se ferme — afficher
// « Consolidation réseau : écriture » se lirait comme un droit de modifier un module qui ne se modifie
// pas. D'où `niveauLabel`, qui redit la règle du serveur (`levelLabel`) plutôt que de la subir.
//
// ⚠️ CLASSES : `.block` / `.blockhead` / `.muted` / `.empty` / `.mono` — les conteneurs RÉELS de la
// feuille de style. `.card` n'en est pas un : c'est la carte de CONNEXION, large de 380 px. Bâtir
// dessus donnait une colonne latérale empilée au-dessus d'un panneau minuscule (retour utilisateur).
import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { dateTimeLong } from '@orgazone/date-format';
import { api, type AccessModuleDto, type RightLevel, type RoleDetail, type RoleRow } from '../api';
import { useAuth, can } from '../auth';
import { EmptyState, alert, confirm, showToast, useOverlayPanel } from '@orgazone/ui-web';
import { formPanelRef, Pill, Spin } from '../ui';

const NIVEAUX: RightLevel[] = ['none', 'read', 'write'];

/** Libellé d'un niveau POUR CE MODULE — voir l'avertissement d'en-tête. */
function niveauLabel(mod: AccessModuleDto, n: RightLevel): string {
  if (!mod.graded) return n === 'none' ? 'Aucun' : 'Accès';
  return n === 'none' ? 'Aucun' : n === 'read' ? 'Lecture' : 'Écriture';
}
/** Niveaux OFFERTS pour ce module : la lecture n'existe pas sur un module tout-ou-rien. */
const niveauxDe = (mod: AccessModuleDto): RightLevel[] => (mod.graded ? NIVEAUX : ['none', 'write']);

const ligneModule: CSSProperties = { display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', padding: '11px 0', borderTop: '1px solid var(--line)' };
const pied: CSSProperties = { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line)' };

// ── Modale : enregistrer, avec la PORTÉE ────────────────────────────────────────────────────────
// ⚠️ La portée est le choix le plus contre-intuitif de l'écran : « modèle seul » fait PLUS de travail,
// pas moins — il ÉPINGLE chaque client qui suit sur ses droits actuels pour qu'il ne bouge pas. On le
// dit en toutes lettres, sinon l'option se choisit à l'envers.
function EnregistrerRole({ role, variances, onClose, onDone }: {
  role: RoleDetail; variances: { label: string; de: string; vers: string }[];
  onClose: () => void; onDone: (notice: string) => void;
}) {
  const [scope, setScope] = useState<'followers' | 'template'>('followers');
  const [reason, setReason] = useState('');       // ⚠️ JAMAIS pré-rempli : un motif suggéré est accepté sans réflexion
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const titleId = useId();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const r = await api.saveRole(role.code, { rights: role.rights, scope, reason: reason.trim() });
      onDone(scope === 'template'
        ? `${r.changed} droit(s) modifié(s) — ${r.pinned} client(s) épinglé(s) sur leurs droits actuels : le changement ne vaut que pour les nouveaux.`
        : `${r.changed} droit(s) modifié(s) — appliqué à ${r.followers} client(s) qui suivent le modèle.`);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modalwrap" onClick={onClose}>
      <form className="modal" ref={formPanelRef(panelRef)} role="dialog" aria-modal="true" aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3 id={titleId}>Enregistrer les droits de « {role.name} »</h3>
        <p className="note" style={{ marginTop: 0 }}>
          Le modèle est une référence vivante : ceux qui le suivent héritent du changement. Ceux qui ont
          personnalisé ce rôle gardent leur version — c’est le prix de la personnalisation, et il est assumé.
        </p>

        <span className="field-title">Ce qui change ({variances.length})</span>
        <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 'var(--t-body)', lineHeight: 1.7 }}>
          {variances.map((d) => <li key={d.label}><strong>{d.label}</strong> : {d.de} → {d.vers}</li>)}
        </ul>

        <label htmlFor="rs">Portée</label>
        <select id="rs" value={scope} onChange={(e) => setScope(e.target.value as 'followers' | 'template')}>
          <option value="followers">Modèle + clients qui suivent</option>
          <option value="template">Modèle seul (futurs clients)</option>
        </select>
        <p className="note" style={{ marginTop: 6 }}>
          {scope === 'template'
            ? 'Les clients existants gardent leurs droits ACTUELS : ils seront épinglés, et ne recevront plus vos prochaines modifications de ce rôle.'
            : 'Les clients qui suivent reçoivent le changement. Ceux qui ont personnalisé ce rôle ne sont pas touchés.'}
        </p>

        <label htmlFor="rr">Motif <span className="opt">(obligatoire — il sera relu)</span></label>
        <input id="rr" value={reason} onChange={(e) => setReason(e.target.value)} required minLength={5}
          placeholder="Retrait de l’accès finances au responsable magasin — demande du comité" />

        {err && <div className="err">{err}</div>}
        <div className="modalfoot">
          <button type="button" className="ghost btn-ghost" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-cta" disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
        <p className="note">⚠️ Une case décochée coupe un accès réel. Le changement est journalisé avec votre compte.</p>
      </form>
    </div>
  );
}

// ── Modale : nouveau rôle ───────────────────────────────────────────────────────────────────────
function NouveauRole({ roles, onClose, onDone }: { roles: RoleRow[]; onClose: () => void; onDone: (code: string) => void }) {
  const [from, setFrom] = useState(roles[0]?.code ?? '');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const titleId = useId();
  // ⚠️ Le focus va au NOM, et APRÈS le socle : un `autoFocus` le devancerait, et serait retenu comme
  // déclencheur à la place du bouton — le focus ne reviendrait alors nulle part à la fermeture.
  const nameField = useRef<HTMLInputElement | null>(null);
  useEffect(() => { nameField.current?.focus(); }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try { const r = await api.createRole({ from, code: code.trim(), name: name.trim() }); onDone(r.code); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modalwrap" onClick={onClose}>
      <form className="modal" ref={formPanelRef(panelRef)} role="dialog" aria-modal="true" aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3 id={titleId}>Nouveau rôle</h3>
        <p className="note" style={{ marginTop: 0 }}>
          Un rôle se crée en partant du plus proche, puis en décochant. Repartir de zéro obligerait à
          retrouver un socle que le modèle connaît déjà — et la première case oubliée est un accès
          manquant que personne ne remarque avant qu’un utilisateur ne soit bloqué.
        </p>
        <label htmlFor="nf">Partir de</label>
        <select id="nf" value={from} onChange={(e) => setFrom(e.target.value)}>
          {roles.map((r) => <option key={r.code} value={r.code}>{r.name} — {r.appLabel}</option>)}
        </select>
        <label htmlFor="nc">Code <span className="opt">(minuscules, chiffres et tirets — il voyage dans les accès)</span></label>
        <input id="nc" className="mono" value={code} onChange={(e) => setCode(e.target.value)} required placeholder="comptable-externe" />
        <label htmlFor="nn">Nom affiché</label>
        <input id="nn" ref={nameField} value={name} onChange={(e) => setName(e.target.value)} required placeholder="Comptable externe" />
        {err && <div className="err">{err}</div>}
        <div className="modalfoot">
          <button type="button" className="ghost btn-ghost" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-cta" disabled={busy}>{busy ? 'Création…' : 'Créer et ouvrir les droits'}</button>
        </div>
        <p className="note">Aucun compte existant n’est touché : un rôle neuf ne s’attribue pas tout seul.</p>
      </form>
    </div>
  );
}

export function Roles() {
  const { user } = useAuth();
  // ⚠️ Sur la PERMISSION, pas sur le rôle. Gardé sur `isSuperadmin` jusqu'au 2026-08-10, l'écran
  // montrait ses boutons alors que la route refusait tout le monde — la divergence que `can()` existe
  // précisément pour empêcher (elle lit les permissions SERVIES par /auth/me).
  const peutEcrire = can(user, 'console.access.write');
  const [rows, setRows] = useState<RoleRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [app, setApp] = useState<'bo' | 'erp'>('bo');
  const [sel, setSel] = useState<string | null>(null);
  const [detail, setDetail] = useState<RoleDetail | null>(null);
  const [brouillon, setBrouillon] = useState<Record<string, RightLevel>>({});
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  // ⚠️ L'échec de lecture est CONSERVÉ : une liste vide ferait croire qu'aucun rôle n'existe, et on en
  // recréerait par-dessus ceux qui sont déjà là.
  const load = useCallback(() => {
    api.roles().then((r) => { setRows(r.roles); setTotal(r.total); setErr(null); }).catch((e) => setErr((e as Error).message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const liste = useMemo(() => (rows ?? []).filter((r) => r.app === app), [rows, app]);
  useEffect(() => { if (liste.length && !liste.some((r) => r.code === sel)) setSel(liste[0]!.code); }, [liste, sel]);

  useEffect(() => {
    if (!sel) { setDetail(null); return; }
    let vivant = true;
    api.role(sel)
      .then((d) => { if (vivant) { setDetail(d); setBrouillon(d.rights); } })
      .catch((e) => { if (vivant) setErr((e as Error).message); });
    return () => { vivant = false; };
  }, [sel]);

  // Écarts entre le brouillon à l'écran et le modèle enregistré — c'est eux qu'on relit avant d'écrire.
  const variances = useMemo(() => {
    if (!detail) return [];
    return detail.modules
      .filter((m) => (brouillon[m.key] ?? 'none') !== (detail.rights[m.key] ?? 'none'))
      .map((m) => ({
        label: m.label,
        de: niveauLabel(m, detail.rights[m.key] ?? 'none'),
        vers: niveauLabel(m, brouillon[m.key] ?? 'none'),
      }));
  }, [detail, brouillon]);

  const remove = async (r: RoleDetail) => {
    const ok = await confirm({
      title: `Supprimer « ${r.name} » ?`,
      message: `Les personnalisations de ce rôle chez les clients sont supprimées avec lui. Les comptes qui le portaient perdent l'accès qu'il ouvrait.`,
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try { await api.deleteRole(r.code); showToast(`Rôle « ${r.name} » supprimé.`); setSel(null); load(); }
    catch (e) { void alert({ title: 'Action refusée', message: (e as Error).message, tone: 'err' }); }
  };

  const realigner = async (tenantId: string, nom: string) => {
    const ok = await confirm({
      title: `Réaligner ${nom} sur le modèle ?`,
      message: `Sa version personnalisée est écrasée : il reprend les droits du modèle et recevra de nouveau vos prochaines modifications. À ne faire qu'avec son accord.`,
      confirmLabel: 'Réaligner',
    });
    if (!ok || !detail) return;
    try {
      await api.realignTenant(detail.code, tenantId);
      showToast(`${nom} suit de nouveau le modèle.`);
      load();
      api.role(detail.code).then(setDetail).catch(() => undefined);
    } catch (e) { void alert({ title: 'Action refusée', message: (e as Error).message, tone: 'err' }); }
  };

  if (err && !rows) return <div className="page"><div className="err">Rôles illisibles : {err}</div></div>;
  if (!rows) return <div className="page"><Spin size={16} /> Lecture…</div>;

  return (
    <div className="page">
      {saving && detail && (
        <EnregistrerRole
          role={{ ...detail, rights: brouillon }} variances={variances}
          onClose={() => setSaving(false)}
          onDone={(n) => { setSaving(false); showToast(n); load(); api.role(detail.code).then((d) => { setDetail(d); setBrouillon(d.rights); }).catch(() => undefined); }}
        />
      )}
      {creating && (
        <NouveauRole roles={rows} onClose={() => setCreating(false)}
          onDone={(code) => { setCreating(false); showToast('Rôle créé — ajustez ses droits puis enregistrez.'); load(); setSel(code); }} />
      )}

      {/* En-tête — même disposition que les autres écrans : action à droite. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Rôles et droits</h1>
          <div className="muted" style={{ fontSize: 'var(--t-body)', marginTop: 3, maxWidth: 720 }}>
            Modèle appliqué aux nouveaux clients. Chacun peut ensuite l’ajuster chez lui — le rôle se
            détache alors du modèle et n’est plus touché par vos modifications.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 4 }}>
          {(['bo', 'erp'] as const).map((a) => (
            <button aria-pressed={app === a} key={a} onClick={() => setApp(a)}
              style={{ height: 30, padding: '0 13px', borderRadius: 'var(--r-xs)', border: '1px solid var(--line)', cursor: 'pointer', fontSize: 'var(--t-body)', fontWeight: app === a ? 700 : 500, background: app === a ? 'var(--panel)' : 'transparent', color: app === a ? 'var(--ink)' : 'var(--mut)' }}>
              {rows.find((r) => r.app === a)?.appLabel ?? a}
            </button>
          ))}
        </div>
        {peutEcrire && <button className="btn btn-cta sm" onClick={() => setCreating(true)}>+ Nouveau rôle</button>}
      </div>

      {liste.length === 0 ? (
        <EmptyState icon="lock" title="Aucun rôle pour cette application"
          note="Les rôles par défaut sont posés au démarrage du service. Si cette liste est vide, l’amorçage n’a pas eu lieu — le journal du service le dit." />
      ) : (
        // ⚠️ `minmax(0,1fr)` sur la 2ᵉ colonne : sans lui, une ligne longue (un motif, un libellé de
        // module) impose sa largeur à la piste et pousse la grille hors de l'écran.
        <div style={{ display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
          {/* Liste latérale */}
          <div className="block" style={{ padding: 8, marginBottom: 0, position: 'sticky', top: 12 }}>
            {liste.map((r) => (
              <button aria-pressed={sel === r.code} key={r.code} onClick={() => setSel(r.code)}
                style={{ display: 'block', width: '100%', padding: '9px 10px', borderRadius: 'var(--r-xs)', border: 0, cursor: 'pointer', textAlign: 'left', background: sel === r.code ? 'var(--bg)' : 'transparent', color: 'inherit' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                  {r.locked && <span title="Rôle indélébile — sans lui, un client peut se retrouver sans administrateur">🔒</span>}
                </span>
                <span className="mono muted" style={{ display: 'block', fontSize: 'var(--t-sub)', marginTop: 2 }}>
                  {r.follow} suivent{r.custom > 0 ? ` · ${r.custom} perso.` : ''}
                </span>
              </button>
            ))}
          </div>

          {/* Matrice des droits */}
          {!detail ? <div className="block"><Spin size={14} /> Lecture du rôle…</div> : (
            <div style={{ minWidth: 0 }}>
              <section className="block">
                <div className="blockhead">
                  <h3>{detail.name}</h3>
                  {detail.locked && <Pill bg="var(--ink100)" fg="var(--ink500)">indélébile</Pill>}
                  <span className="mono muted">{detail.app}:{detail.code}</span>
                  {peutEcrire && !detail.locked && (
                    <button className="ghost btn-ghost" style={{ width: 'auto', marginLeft: 'auto' }} onClick={() => void remove(detail)}>Supprimer</button>
                  )}
                </div>
                <p className="muted" style={{ fontSize: 'var(--t-body)', margin: '0 0 4px', maxWidth: 760 }}>{detail.description}</p>
                {detail.updatedBy && (
                  <p className="muted" style={{ fontSize: 'var(--t-note)', margin: 0 }}>
                    Dernière modification {dateTimeLong(detail.updatedAt)} · {detail.updatedBy}
                  </p>
                )}

                <div style={{ marginTop: 12 }}>
                  {detail.modules.map((m) => (
                    <div key={m.key} style={ligneModule}>
                      <div style={{ minWidth: 220, flex: 1 }}>
                        <div style={{ fontSize: 'var(--t-lead)', fontWeight: 600 }}>{m.label}</div>
                        <div className="muted" style={{ fontSize: 'var(--t-note)', marginTop: 2 }}>{m.note}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        {niveauxDe(m).map((n) => {
                          const actif = (brouillon[m.key] ?? 'none') === n;
                          return (
                            <button key={n} disabled={!peutEcrire}
                              onClick={() => setBrouillon((b) => ({ ...b, [m.key]: n }))}
                              style={{ height: 30, padding: '0 12px', borderRadius: 'var(--r-xs)', cursor: peutEcrire ? 'pointer' : 'not-allowed', fontSize: 'var(--t-dense)', fontWeight: actif ? 700 : 500, border: `1px solid ${actif ? 'var(--teal)' : 'var(--line)'}`, background: actif ? 'var(--panel)' : 'transparent', color: actif ? 'var(--teal)' : 'var(--mut)' }}>
                              {niveauLabel(m, n)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {peutEcrire && (
                  <div style={pied}>
                    <span className="muted" style={{ fontSize: 'var(--t-body)' }}>
                      {variances.length === 0 ? 'Aucune modification' : `${variances.length} droit(s) modifié(s)`}
                    </span>
                    <div style={{ flex: 1 }} />
                    {variances.length > 0 && (
                      <button className="ghost btn-ghost" style={{ width: 'auto' }} onClick={() => setBrouillon(detail.rights)}>Annuler les modifications</button>
                    )}
                    <button className="btn btn-cta sm" disabled={variances.length === 0} onClick={() => setSaving(true)}>Enregistrer</button>
                  </div>
                )}
              </section>

              {/* DIVERGENCE — ceux qui ne suivent plus, et sur quoi exactement. */}
              {detail.customised.length > 0 && (
                <section className="block">
                  <div className="blockhead">
                    <h3>Clients ayant personnalisé ce rôle</h3>
                    <span className="count">{detail.customised.length}</span>
                  </div>
                  <p className="muted" style={{ fontSize: 'var(--t-body)', margin: '0 0 4px', maxWidth: 760 }}>
                    Ils ne suivent plus le modèle : <strong>vos modifications ne les atteignent pas</strong>.
                    Vous pouvez les réaligner, l’écart est affiché avant écriture.
                  </p>
                  {detail.customised.map((c) => (
                    <div key={c.tenantId} style={{ borderTop: '1px solid var(--line)', padding: '11px 0' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 'var(--t-lead)' }}>{c.tenantName}</strong>
                        <span className="muted" style={{ fontSize: 'var(--t-note)' }}>
                          {c.diff.length} écart(s) · épinglé {dateTimeLong(c.pinnedAt)}{c.pinnedBy ? ` · ${c.pinnedBy}` : ''}
                        </span>
                        <div style={{ flex: 1 }} />
                        {peutEcrire && (
                          <button className="ghost btn-ghost" style={{ width: 'auto' }} onClick={() => void realigner(c.tenantId, c.tenantName)}>Réaligner sur le modèle</button>
                        )}
                      </div>
                      {c.reason && <div className="muted" style={{ fontSize: 'var(--t-note)', marginTop: 4 }}>Motif : {c.reason}</div>}
                      <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 'var(--t-body)', lineHeight: 1.7 }}>
                        {c.diff.map((d) => <li key={d.module}><strong>{d.label}</strong> : {d.fromLabel} → {d.toLabel}</li>)}
                      </ul>
                    </div>
                  ))}
                </section>
              )}

              <p className="muted" style={{ fontSize: 'var(--t-note)' }}>
                {total} client(s) au total · {liste.find((r) => r.code === detail.code)?.follow ?? 0} suivent ce rôle
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
